import React, { FC, ReactElement, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  FormInstance,
  Input,
  Rate,
  Row,
  Typography,
  message,
} from "antd";
import { SendOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { ReviewResponse, ReviewError } from "../../../types/types";

import "./ProductReviews.css";
import { selectUserFromUserState } from "../../../redux-toolkit/user/user-selector";

interface ReviewData {
  author: string;
  message: string;
  rating: number;
}

type PropType = {
  reviews: ReviewResponse[];
  reviewErrors: Partial<ReviewError>;
  addReview: (data: ReviewData) => void | Promise<void>;
  form?: FormInstance<any>;
};

const ProductReviews: FC<PropType> = ({
  reviews,
  reviewErrors,
  addReview,
  form,
}): ReactElement => {
  const { messageError, ratingError } = reviewErrors;

  // User from Redux
  const currentUser = useSelector(selectUserFromUserState);
  const isLoggedIn = Boolean(currentUser && currentUser.email);

  // Loading for posting review
  const [posting, setPosting] = useState(false);

  // Compute display name to send to backend
  const displayName = isLoggedIn
    ? currentUser?.firstName ||
      currentUser?.email?.split("@")[0] ||
      "User"
    : "";

  // Reset rating & message after posting
  const onFinish = async (values: any) => {
    const payload: ReviewData = {
      author: displayName, // hidden from UI but sent to backend
      rating: values.rating,
      message: values.message || "",
    };

    try {
      setPosting(true);

      const maybePromise = addReview(payload);
      if (maybePromise && typeof (maybePromise as any).then === "function") {
        await maybePromise;
      }

      message.success("Review submitted successfully!");
      form?.resetFields(["rating", "message"]);

    } catch (err: any) {
      console.error(err);
      message.error(err?.message || "Failed to submit review.");
    } finally {
      setPosting(false);
    }
  };

  const getInitial = (name?: string) =>
    name && name.length > 0 ? name.charAt(0).toUpperCase() : "U";

  return (
    <>
      {/* ===== Add Review Form (ONLY WHEN LOGGED IN) ===== */}
      {isLoggedIn && (
        <Row>
          <Col span={24}>
            <Form form={form} layout="vertical" onFinish={onFinish}>
              <Card className="review-card">

                {/* Rating */}
                <Typography.Text>Your rating *</Typography.Text>
                <Form.Item
                  name="rating"
                  help={ratingError}
                  validateStatus={ratingError ? "error" : ""}
                  rules={[{ required: true, message: "Please provide a rating" }]}
                >
                  <Rate />
                </Form.Item>

                {/* Message */}
                <Typography.Text>Message</Typography.Text>
                <Form.Item
                  name="message"
                  help={messageError}
                  validateStatus={messageError ? "error" : ""}
                >
                  <Input.TextArea
                    rows={4}
                    placeholder="Share your experience..."
                  />
                </Form.Item>

                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SendOutlined />}
                  loading={posting}
                >
                  Post a review
                </Button>
              </Card>
            </Form>
          </Col>
        </Row>
      )}
      {/* ===== Reviews List BEFORE form ===== */}
      <Row>
        <Col span={24}>
          {reviews.map((review) => {
            const safeAuthor = review?.author || "Unknown User";
            const initial = getInitial(safeAuthor);         // <-- FIX
            const dateText = review?.date
              ? new Date(review.date).toLocaleDateString()
              : "";

            return (
              <Card key={review.id} className="fk-review-card">
                <div className="fk-review-top-row">
                  <div className="fk-review-avatar">{initial}</div>

                  <div className="fk-review-name">{safeAuthor}</div>

                  <div className="fk-review-stars">
                    <Rate disabled value={review.rating} />
                  </div>

                  <div className="fk-review-date">{dateText}</div>
                </div>

                <div className="fk-review-text">{review.message}</div>
              </Card>
            );
          })}
        </Col>
      </Row>
    </>
  );
};

export default ProductReviews;
